import { useMemo } from "react";

import { addDays, subWeeks } from "date-fns";
import { useTranslation } from "react-i18next";

export type AdherenceView = "one_week" | "eight_weeks" | "twelve_weeks";

export const useFilterOptions = () => {
  const { t } = useTranslation();

  const memoized = useMemo(() => {
    const today = new Date();

    const weeksConfig: { key: AdherenceView; label: string; weeks: number; offsetDays?: number }[] = [
      { key: "one_week", label: `1 ${t("common.week", { count: 1 })}`, weeks: 1, offsetDays: 1 },
      { key: "eight_weeks", label: `8 ${t("common.week", { count: 8 })}`, weeks: 8, offsetDays: 1 },
      { key: "twelve_weeks", label: `12 ${t("common.week", { count: 12 })}`, weeks: 12, offsetDays: 1 },
    ];

    const options = weeksConfig.map(({ key, label, weeks, offsetDays }) => {
      const date = addDays(subWeeks(today, weeks), offsetDays ?? 0);
      return {
        option: { label, value: key },
        startTime: new Date(
          Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds()
          )
        ).toISOString(),
        key,
        weeks,
      };
    });

    const endTime = new Date(
      Date.UTC(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        today.getHours(),
        today.getMinutes(),
        today.getSeconds()
      )
    ).toISOString();

    const barCounts: Record<AdherenceView, number> = {
      one_week: 7,
      eight_weeks: 8,
      twelve_weeks: 12,
    };

    return {
      weekOptions: options.map(o => o.option),
      startTimes: Object.fromEntries(options.map(o => [o.key, o.startTime])) as Record<AdherenceView, string>,
      endTime,
      barCounts,
    };
  }, []);

  return {
    defaultView: "eight_weeks" as AdherenceView,
    weekOptions: memoized.weekOptions,
    startTimes: memoized.startTimes,
    endTime: memoized.endTime,
    barCounts: memoized.barCounts,
  };
};
